<template>
	<div class="@container flex-1 flex h-full min-h-0">

		<!-- List of collections (hidden on narrow screens) -->
		<LessonPlanCollectionsList
			class="hidden @3xl:block @3xl:w-96 h-full border-r border-base-200 min-h-0 overflow-y-auto"
			:selectedCollection="showFilterInCaseOfWideScreen ? null : collectionIfSingleCollectionFilter"
			@selectionChanged="handleCollectionSelection"
		/>

		<!-- Main content area -->
		<div class="w-full overflow-y-auto relative">
			<div class="w-full px-4 max-w-4xl">
				<FloatingPlusButton v-if="!readOnly" @push="handleCreateIntent" />

				<div class="h-auto">

					<div class="sticky top-0 z-10 bg-base/50 backdrop-blur-md pt-4">
						<LessonPlanMyLibraryListHeader
							:selectedCollection="showFilterInCaseOfWideScreen ? null : collectionIfSingleCollectionFilter"
							:resetModals="(selectedLessonPlanToView !== null || selectedLessonPlanToEdit !== null)"
							:readOnly="readOnly"
							class="hidden @3xl:flex mr-24"
						/>

						<div class="flex items-start rounded-lg px-1 py-1 transition-colors" :class="[ showFilterInCaseOfWideScreen? '@3xl:bg-indigo-50' : '' ]">
							<button @click="toggleFilter" class="hidden @3xl:flex shrink-0 btn btn-sm btn-ghost hover:bg-base-100" :class="[ showFilterInCaseOfWideScreen? 'text-indigo-400 hover:bg-indigo-100' : '' ]">
								<!-- phosphor icon - funnel-x -->
								<svg v-show="showFilterInCaseOfWideScreen" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 256 256">
									<path d="M227.82,66.76A16,16,0,0,0,216,40H40A16,16,0,0,0,28.19,66.76l.08.09L96,139.17V216a16,16,0,0,0,24.87,13.32l32-21.34A16,16,0,0,0,160,194.66V139.17l67.73-72.32ZM40,56h0Zm106.19,74.59A8,8,0,0,0,144,136v58.66L112,216V136a8,8,0,0,0-2.16-5.46L40,56H216Zm99.49,79.81a8,8,0,0,1-11.32,11.32L216,203.32l-18.34,18.35a8,8,0,0,1-11.31-11.32L204.69,192l-18.34-18.35a8,8,0,0,1,11.31-11.31L216,180.69l18.34-18.34a8,8,0,0,1,11.32,11.31L227.31,192Z"></path>
								</svg>
								<!-- phosphor icon - funnel -->
								<svg v-show="!showFilterInCaseOfWideScreen" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 256 256">
									<path d="M230.6,49.53A15.81,15.81,0,0,0,216,40H40A16,16,0,0,0,28.19,66.76l.08.09L96,139.17V216a16,16,0,0,0,24.87,13.32l32-21.34A16,16,0,0,0,160,194.66V139.17l67.74-72.32.08-.09A15.8,15.8,0,0,0,230.6,49.53ZM40,56h0Zm106.18,74.58A8,8,0,0,0,144,136v58.66L112,216V136a8,8,0,0,0-2.16-5.47L40,56H216Z"></path>
								</svg>
							</button>
							<LessonPlanFilter
								class="block mt-2 @3xl:mt-0 @3xl:pr-12" :class="[!showFilterInCaseOfWideScreen? '@3xl:hidden' : '']"
								:filter="filter"
								fieldClasses="bg-base-100 @3xl:bg-base"
								@change="handleChangedFilter"
								@reset="resetFilter" />
						</div>
					</div>

					<template v-if="lessonPlansStore.loaded && lessonPlansStore.items?.length === 0">
						<EmptyState v-if="collectionIfSingleCollectionFilter" :title="$t('No lesson plans yet in this collection')" />
						<EmptyState v-else-if="!filter.isEmpty()" :title="$t('No lesson plans found')" />
						<EmptyState v-else :title="$t('No lesson plans yet in your library')" :description="$t('Start with creating your first lesson plan')" />
					</template>

					<LessonPlanList
						:lessonPlans="lessonPlansStore.items"
						:showCreatorName="(lessonPlan) => lessonPlan.creator?.id !== usersStore?.me?.id || lessonPlan.originalCreator?.id !== usersStore?.me?.id"
						@lessonPlanClicked="handleViewIntent">
					</LessonPlanList>
				</div>
			</div>
		</div>

		<LessonPlanViewer
			v-if="selectedLessonPlanToView"
			:initialWidth="lessonPlanViewerWidth"
			:withEditButton="!readOnly && selectedLessonPlanToView.canBeEditedBy(usersStore.me)"
			:withSelectButtons="lessonPlanViewerWithSelectButtons"
			:lessonPlan="selectedLessonPlanToView"
			:skipOpenAnimation="skipViewerAnimation"
			:withLibraryButton="!readOnly"
			:closeOnRemovedFromLibrary="true"
			:collectionTooltipMessage="lpViewerTooltipMessage"
			@requestUnmount="closeViewer"
			@editLessonPlan="handleEditIntent"
			@selectLessonPlan="$emit('selectLessonPlan', $event)"
			@createVariant="onCreateVariant"
		/>

		<LessonPlanEditor
			v-if="showEditor"
			:lessonPlan="selectedLessonPlanToEdit"
			:selectedCollection="collectionIfSingleCollectionFilter"
			:skipOpenAnimation="skipEditorAnimation"
			@requestClose="closeEditor"
			@cancel="handleEditorCancel"
			@saved="handleEditorSaved"
		/>
	</div>
</template>


<script lang="ts">
import LessonPlanCollectionsList from "@/components/LessonPlanCollectionsList.vue";
import LessonPlanViewer from "@/components/LessonPlanViewer.vue";
import LessonPlanEditor from "@/components/LessonPlanEditor.vue";
import FloatingPlusButton from "@/components/ui/FloatingPlusButton.vue";
import LessonPlanMyLibraryListHeader from "@/components/LessonPlanMyLibraryListHeader.vue";
import LessonPlanList from "@/components/LessonPlanList.vue";

import { mapStores } from "pinia";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useUsersStore } from "@/stores/Users.store";
import { LessonPlan } from "@/models/LessonPlan.model";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import LessonPlanFilter from "@/components/LessonPlanFilter.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import { isLessonPlanLibraryRoute } from "@/utils/RouteContext.util";


export default {
	name: 'LessonPlanMyLibrary',

	emits: [
		'newLessonPlanRequested',
		'selectLessonPlan',
		'createVariant',
	],

	props: {
		readOnly: {
			type: Boolean,
			default: false,
		},
		lessonPlanViewerWidth: {
			type: String,
			default: '3xl',
		},
		lessonPlanViewerWithSelectButtons: {
			type: Boolean,
			default: false,
		},
		selectedCollectionId: {
			type: String,
			default: null,
		},
	},

	components: {
		LessonPlanCollectionsList,
		LessonPlanViewer,
		LessonPlanEditor,
		FloatingPlusButton,
		LessonPlanMyLibraryListHeader,
		LessonPlanFilter,
		LessonPlanList,
		EmptyState,
	},

	data() {
		return {
			selectedLessonPlanToEdit: null as LessonPlan | null,
			selectedLessonPlanToView: null as LessonPlan | null,
			showEditor: false,
			skipEditorAnimation: false,
			skipViewerAnimation: false,
			lpViewerTooltipMessage: '',	 // tooltip message for the "collection selector button" to show in the viewer
			pendingVariant: null as LessonPlan | null,	// a variant to open in the editor once the viewer has closed
			showFilterInCaseOfWideScreen: false, // On narrow screens, the filter is always shown. On wide screens however,
												 // the filter is hidden by default but can be toggled with a button.
												 // If a single collection is selected in the sidebar, the filter gets hidden again
												 // and the collection name is shown prominently instead.
												 // This boolean works along @container queries to determine whether the filter should be shown or not on wide screens.
		};
	},

	computed: {
		...mapStores(useLessonPlansStore, useLessonPlanCollectionsStore, useUsersStore),

		filter() {
			return this.lessonPlansStore.getCurrentFilter();
		},

		// If the current filter has exactly 1 collection selected, return that collection.
		collectionIfSingleCollectionFilter() {
			if(this.filter && this.filter.getParamValues('collections').length === 1) {
				const collectionId = this.filter.getParamValues('collections')[0].id;
				return this.lessonPlanCollectionsStore.find(collectionId);
			}
			return null;
		},
	},

	watch: {
		// watcher for the id that is being passed as a prop by the router
		selectedCollectionId() {
			this.showFilterInCaseOfWideScreen = false;
			this.updateListViewForSelectedCollection();
		},
	},

	async mounted() {
		await this.lessonPlanCollectionsStore.loadOnce();

		if (this.selectedCollectionId || !this.lessonPlansStore.hasDefaultFilterApplied) {
			// The route (or a filter left over from a previous mount) requires a filtered
			// (re)load — applyFilter()/resetFilter() already trigger one, so no loadOnce()
			// first: that would fire an unfiltered request only to immediately replace it.
			this.updateListViewForSelectedCollection();
		} else {
			await this.lessonPlansStore.loadOnce();
		}
	},

	methods: {

		toggleFilter() {
			this.showFilterInCaseOfWideScreen = !this.showFilterInCaseOfWideScreen;
			if(this.showFilterInCaseOfWideScreen) {
			} else if(!this.filter?.isEmpty()) {
				this.resetFilter();
			}
		},

		async handleChangedFilter(filter: any) {
			this.lessonPlansStore.applyFilter(filter);
		},

		async resetFilter() {
			this.lessonPlansStore.resetFilter();
			if(isLessonPlanLibraryRoute(this.$router)) {
				// After resetting the filter, also "reset" the route so it matches the content
				this.$router.push({ name: "lesson-plan-library.my-library" });
			}
		},

		/**
		 * Sync the store filter with the route. Idempotent: when no collection is selected
		 * and the filter is already at its default, this loads nothing — so callers that
		 * reset the filter themselves before navigating (e.g. navigateToAllLessonPlans in
		 * the list header) don't cause a second, identical request via the route watcher.
		 */
		async updateListViewForSelectedCollection() {
			if (this.selectedCollectionId) {
				const collection = this.lessonPlanCollectionsStore.find(this.selectedCollectionId);
				if (collection) {
					const filter = this.lessonPlansStore.getDefaultFilter();
					filter.addParamValue('collections', collection.asFilterParamValue());
					this.lessonPlansStore.applyFilter(filter);
				} else {
					console.warn(`Collection with ID ${this.selectedCollectionId} not found`);
				}
			} else if (!this.lessonPlansStore.hasDefaultFilterApplied) {
				// Navigated to the plain list while a collection filter is still applied
				// (e.g. browser back from a collection route).
				this.lessonPlansStore.resetFilter();
			}
		},

		handleCreateIntent() {
			this.showEditor = true;
		},

		handleEditIntent(lessonPlan: LessonPlan) {
			// Close the viewer first, then open the editor (no animation — instant swap)
			this.selectedLessonPlanToView = null;
			this.skipEditorAnimation = true;
			this.selectedLessonPlanToEdit = lessonPlan;
			this.showEditor = true;
		},

		handleEditorCancel(lessonPlan: LessonPlan) {
			// Swap back to the viewer instantly (no animation)
			this.showEditor = false;
			this.selectedLessonPlanToEdit = null;
			this.skipEditorAnimation = false;
			this.skipViewerAnimation = true;
			this.lpViewerTooltipMessage = '';
			this.selectedLessonPlanToView = lessonPlan;
		},

		handleEditorSaved(savedLessonPlan: LessonPlan, createOrUpdate: string) {
			// Immediately remove the editor (prevent its 300ms close animation)
			this.showEditor = false;
			this.selectedLessonPlanToEdit = null;
			this.skipEditorAnimation = false;

			// Open the viewer instantly to show the saved plan
			this.skipViewerAnimation = true;
			this.selectedLessonPlanToView = savedLessonPlan;

			const selectedCollection = this.collectionIfSingleCollectionFilter;

			if(createOrUpdate === 'create' && savedLessonPlan.isInMyLibrary) {
				if(selectedCollection) {
					this.lpViewerTooltipMessage = this.$t("Your new lesson plan was added to the collection: {listName}", { listName: '<p class="collection">'+selectedCollection.name+'</p>' });
				} else {
					this.lpViewerTooltipMessage = this.$t('Add this lesson plan to some library collections?');
				}
			}
		},

		async closeEditor() {
			this.showEditor = false;
			this.skipEditorAnimation = false;
			this.selectedLessonPlanToEdit = null;
		},

		async handleCollectionSelection(collection: LessonPlanCollection) {
			this.$router.push({ name: "lesson-plan-library.my-library.collection", params: { collectionId: collection.id } });
		},

		async handleLoadMore() {
			try {
				await this.lessonPlansStore.loadMore();
			} catch (error) {
				console.error("Failed to load more lesson plans:", error);
			}
		},

		handleViewIntent(lessonPlan: LessonPlan) {
			// Simply set the lesson plan to view directly
			this.selectedLessonPlanToView = lessonPlan;
		},

		closeViewer() {
			this.skipViewerAnimation = false;
			this.lpViewerTooltipMessage = '';
			this.selectedLessonPlanToView = null;

			// "Create variant" was chosen in the viewer: now that it has closed, edit the variant
			if (this.pendingVariant) {
				this.selectedLessonPlanToEdit = this.pendingVariant;
				this.pendingVariant = null;
				this.showEditor = true;
			}
		},

		onCreateVariant(lessonPlan: LessonPlan) {
			// In the agenda, LessonActivities has its own flow for a variant (it also assigns
			// the result to the lesson item): just pass the request on.
			if (!isLessonPlanLibraryRoute(this.$router)) {
				this.$emit('createVariant', lessonPlan);
				return;
			}

			// The viewer is already animating itself closed (see LessonPlanViewer.confirmCreateVariant):
			// remember the variant, closeViewer (fired by @requestUnmount) opens the editor with it.
			// Once saved, handleEditorSaved shows the new variant in the viewer.
			this.pendingVariant = lessonPlan.createVariantFor(this.usersStore.me);
		},
	},
}

</script>
